// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Main accounting close wizard component.
 *
 * Provides a step-by-step workflow for the month-end accounting close process.
 * Integrates with backend APIs for creating, viewing, and locking close runs.
 */
import { createCloseRun } from '@/accounting/api/create-close-run';
import { lockCloseRun } from '@/accounting/api/lock-close-run';
import {
  AccountingCloseStep,
  CloseRunResponse,
  JournalEntryResponse,
  JournalEntryType,
  JournalEntryWithReviewState,
  SavedAccountingPeriodActivityStatus,
  WorkflowReviewState,
} from '@/accounting/api/types';
import { AccountingPeriodSelector } from '@/accounting/components/accounting-period-selector';
import { CloseRunInfoBar } from '@/accounting/components/close-run-info-bar';
import { CloseRunLoader } from '@/accounting/components/close-run-loader';
import { ConfirmLockStep } from '@/accounting/components/confirm-lock-step';
import { FixedAssetRollforwardStep } from '@/accounting/components/fixed-asset-rollforward-step';
import { InventoryRollforwardStep } from '@/accounting/components/inventory-rollforward-step';
import { PriorPeriodAdjustmentsStep } from '@/accounting/components/prior-period-adjustments-step';
import { VerifyDetailsStep } from '@/accounting/components/verify-details-step';
import { useAccountingPeriods } from '@/accounting/hooks/use-accounting-periods';
import { useInitialTrueUps } from '@/accounting/hooks/use-initial-true-ups';
import {
  findDefaultPeriod,
  formatPeriod,
  generatePeriodList,
} from '@/accounting/utilities/generate-period-list';
import { hasLockedActivity } from '@/accounting/utilities/has-locked-activity';
import { findEarliestInitialTrueUpPeriod } from '@/accounting/utilities/initial-true-ups';
import { toPeriodKey } from '@/accounting/utilities/to-period-key';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { Link } from '@/components/link';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  Check,
  DownloadRounded,
  InfoOutlined,
} from '@/constants/hardfin-standard-icons';
import { useDownloadFileByKey } from '@/hooks/use-download-file-by-key';
import { ReportBreadcrumbs } from '@/report/components/report-breadcrumbs';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Step,
  StepButton,
  StepConnector,
  StepIconProps,
  StepLabel,
  Stepper,
  Typography,
  stepConnectorClasses,
  styled,
  useTheme,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AxiosError } from 'axios';
import { useMemo, useRef, useState } from 'react';

/**
 * Converts API journal entry responses to UI journal entry format with review state.
 * Adds the frontend-only 'reviewed' field to track user review status.
 */
function convertApiEntriesToUiEntries(
  apiEntries: JournalEntryResponse[],
  entryTypeFilter: JournalEntryType[]
): JournalEntryWithReviewState[] {
  return apiEntries
    .filter((entry) => entryTypeFilter.includes(entry.entryType))
    .map((entry) => ({
      ...entry,
      reviewed: false,
    }));
}

/**
 * Categorizes journal entries from API response into inventory, fixed asset, and adjustment groups.
 * Uses backend enum values (e.g., INVENTORY_PURCHASES) not display names.
 */
function categorizeJournalEntries(
  closeRun: CloseRunResponse
): WorkflowReviewState {
  const inventoryTypes: JournalEntryType[] = [
    'INVENTORY_PURCHASES',
    'INVENTORY_SALE',
    'INVENTORY_SCRAP',
  ];
  const fixedAssetTypes: JournalEntryType[] = [
    'INVENTORY_TO_FIXED_ASSETS',
    'DEPRECIATION',
    'FIXED_ASSET_SALE',
    'FIXED_ASSET_SCRAP',
  ];
  const adjustmentTypes: JournalEntryType[] = ['ADJUSTMENT'];

  // Flatten journal entries across every currency bucket into a single union.
  // Each entry carries its own currency, so the review-state stays a flat union
  // keyed by JE id and never nets amounts across currencies.
  const allEntries = closeRun.byCurrency.flatMap(
    (bucket) => bucket.journalEntries
  );

  return {
    inventoryEntries: convertApiEntriesToUiEntries(allEntries, inventoryTypes),
    fixedAssetEntries: convertApiEntriesToUiEntries(
      allEntries,
      fixedAssetTypes
    ),
    adjustmentEntries: convertApiEntriesToUiEntries(
      allEntries,
      adjustmentTypes
    ),
  };
}

const STEPS = [
  {
    id: 1 as AccountingCloseStep,
    label: 'Prepare details',
    description: 'Confirm all accounting details are recorded',
  },
  {
    id: 2 as AccountingCloseStep,
    label: 'Inventory rollforward',
    description: 'Review inventory changes and journal entries',
  },
  {
    id: 3 as AccountingCloseStep,
    label: 'Fixed asset rollforward',
    description: 'Review fixed asset changes and journal entries',
  },
  {
    id: 4 as AccountingCloseStep,
    label: 'Prior period adjustments',
    description: 'Review adjustments for prior-period deltas',
  },
  {
    id: 5 as AccountingCloseStep,
    label: 'Confirm and lock',
    description: 'Finalize and lock the period',
  },
];

// Initial empty state - entries will be populated from API after close run creation
const initialReviewState: WorkflowReviewState = {
  inventoryEntries: [],
  fixedAssetEntries: [],
  adjustmentEntries: [],
};

/**
 * Custom connector for the stepper, adjusted for larger step icons.
 */
const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 15, // Center with 40px icons
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 0,
    backgroundColor: theme.palette.divider,
  },
}));

/**
 * Custom step icon with larger circles and bolder numbers.
 */
const CustomStepIcon = (props: StepIconProps) => {
  return (
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: '50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontWeight: 700,
        fontSize: '18px',
        backgroundColor: props.completed
          ? 'success.main'
          : props.active
            ? 'primary.main'
            : 'background.paper',
        border: (theme) =>
          `2px solid ${
            props.completed
              ? theme.palette.success.main
              : props.active
                ? theme.palette.primary.main
                : theme.palette.divider
          }`,
        color: props.completed || props.active ? 'white' : 'text.secondary',
      }}
    >
      {props.completed ? <Check /> : props.icon}
    </Box>
  );
};

export function AccountingCloseWizard() {
  const downloadSummary = useDownloadFileByKey();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { user } = useCurrentUser();
  const organizationTimezone = user.organization.timezone;

  // Fetch the org's initial true-ups to determine if wizard can be used
  const { initialTrueUps, isLoading: isLoadingInitialTrueUp } =
    useInitialTrueUps();

  // The period list starts at the first initial true-up, so later closes and later currencies' initial true-ups stay visible
  const earliestInitialTrueUpPeriod =
    findEarliestInitialTrueUpPeriod(initialTrueUps);
  const hasLockedInitialTrueUp = earliestInitialTrueUpPeriod !== null;
  // Extract primitives from the initial true-up period for stable dependency references
  const initialTrueUpYear = earliestInitialTrueUpPeriod?.year;
  const initialTrueUpMonth = earliestInitialTrueUpPeriod?.month;

  // Fetch accounting periods to determine the next period to close
  const { backendPeriods } = useAccountingPeriods();

  // Compute the next period that should be closed (oldest unlocked period)
  const nextPeriodToClose = useMemo(() => {
    if (initialTrueUpYear === undefined || initialTrueUpMonth === undefined) {
      return null;
    }
    const initialTrueUpPeriod = {
      year: initialTrueUpYear,
      month: initialTrueUpMonth,
    };
    const periods = generatePeriodList(backendPeriods, organizationTimezone, {
      initialTrueUpPeriod,
    });
    return findDefaultPeriod(periods);
  }, [
    backendPeriods,
    organizationTimezone,
    initialTrueUpYear,
    initialTrueUpMonth,
  ]);

  // Selected period state - managed by the period selector component
  const [selectedPeriod, setSelectedPeriod] =
    useState<SavedAccountingPeriodActivityStatus | null>(null);

  // Check if the selected period is not the next sequential period to close
  const isPeriodNotSequential = useMemo(() => {
    if (!selectedPeriod || !nextPeriodToClose) {
      return false;
    }
    // If the selected period is locked, it's not sequential (handled separately)
    if (hasLockedActivity(selectedPeriod)) {
      return false;
    }

    // Compare selected period with the next period to close
    return (
      selectedPeriod.year !== nextPeriodToClose.year ||
      selectedPeriod.month !== nextPeriodToClose.month
    );
  }, [selectedPeriod, nextPeriodToClose]);

  // Check if the selected period has not yet completed (current or future month)
  const isPeriodNotCompleted = useMemo(() => {
    if (!selectedPeriod) {
      return false;
    }
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // JavaScript months are 0-indexed

    // Period is not completed if it's the current month or in the future
    if (selectedPeriod.year > currentYear) {
      return true;
    }
    if (
      selectedPeriod.year === currentYear &&
      selectedPeriod.month >= currentMonth
    ) {
      return true;
    }
    return false;
  }, [selectedPeriod]);

  const [currentStep, setCurrentStep] = useState<AccountingCloseStep>(1);
  const [completedSteps, setCompletedSteps] = useState<
    Set<AccountingCloseStep>
  >(new Set());

  // Close run state - stores the created close run from the API
  const [closeRunId, setCloseRunId] = useState<string | null>(null);
  const [closeRun, setCloseRun] = useState<CloseRunResponse | null>(null);
  const [isCreatingCloseRun, setIsCreatingCloseRun] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // A close covers every asset currency, so one without an initial true-up blocks the wizard up front.
  const missingInitialTrueUpCurrencies =
    initialTrueUps?.missingCurrencies ?? [];

  // Ref for scrolling to top when changing steps
  const topAnchorRef = useRef<HTMLDivElement>(null);

  /**
   * Scrolls the page to the top anchor element.
   * Called when navigating between workflow steps.
   */
  const scrollToTop = () => {
    topAnchorRef.current?.scrollIntoView({ behavior: 'instant' });
  };

  // Handle period selection change from the period selector
  const handlePeriodChange = (period: SavedAccountingPeriodActivityStatus) => {
    setSelectedPeriod(period);
    // Reset workflow state when period changes
    setCurrentStep(1);
    setCompletedSteps(new Set());
    setCloseRunId(null);
    setCloseRun(null);
    setShowErrorSnackbar(false);
    setErrorMessage(null);
    setReviewState(initialReviewState);
  };

  // Centralized workflow state for journal entry tracking
  const [reviewState, setReviewState] =
    useState<WorkflowReviewState>(initialReviewState);

  const updateInventoryEntries = (entries: JournalEntryWithReviewState[]) => {
    setReviewState((prev) => ({ ...prev, inventoryEntries: entries }));
  };

  const updateFixedAssetEntries = (entries: JournalEntryWithReviewState[]) => {
    setReviewState((prev) => ({ ...prev, fixedAssetEntries: entries }));
  };

  const updateAdjustmentEntries = (entries: JournalEntryWithReviewState[]) => {
    setReviewState((prev) => ({ ...prev, adjustmentEntries: entries }));
  };

  const handleNext = async () => {
    // When moving past step 1 (verify details), create the close run
    if (currentStep === 1 && !closeRunId) {
      if (!selectedPeriod) {
        setShowErrorSnackbar(true);
        return;
      }

      setIsCreatingCloseRun(true);
      setShowErrorSnackbar(false);
      setErrorMessage(null);

      try {
        const response = await createCloseRun({
          year: selectedPeriod.year,
          month: selectedPeriod.month,
        });

        setCloseRunId(response.closeRun.id);
        setCloseRun(response.closeRun);

        // Update review state with journal entries from API
        const categorizedEntries = categorizeJournalEntries(response.closeRun);
        setReviewState(categorizedEntries);

        setCompletedSteps((prev) => new Set(prev).add(currentStep));
        setCurrentStep(2);
        scrollToTop();
      } catch (error) {
        const apiError = handleAPIError(error as AxiosError);
        // Reset both surfaces so a retry never leaves stale state
        // from a previous attempt visible alongside the new one.
        setErrorMessage(null);
        setShowErrorSnackbar(false);

        if (
          apiError.apiStatusCode ===
          ERROR_CODE_MAPPING.accounting_period_not_sequential
        ) {
          // User-friendly message for non-sequential period
          setErrorMessage(
            'You must close periods in order. Please select the next unlocked period after your most recently locked period.'
          );
        } else if (
          apiError.apiStatusCode ===
          ERROR_CODE_MAPPING.accounting_period_already_locked
        ) {
          // User-friendly message for already locked case
          setErrorMessage(
            'This period has already been locked. Please refresh the page to see the current status.'
          );
        } else if (
          apiError.apiStatusCode ===
          ERROR_CODE_MAPPING.accounting_period_not_completed
        ) {
          // User-friendly message for period not yet completed
          setErrorMessage(
            'This period has not yet ended. You can only close periods that are in the past.'
          );
        } else {
          // Other backend errors: surface a curated message if one
          // exists, else fall back to the support snackbar so backend
          // text never leaks into the UI. Curated codes are the handled
          // set and skip Sentry; the fallback path fires Sentry.
          const curated = getUserFacingErrorMessage(apiError);
          if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
            setErrorMessage(curated);
          } else {
            if (!apiError.sentToSentry) {
              captureException(error);
            }
            setShowErrorSnackbar(true);
          }
        }
      } finally {
        setIsCreatingCloseRun(false);
      }
      return;
    }

    setCompletedSteps((prev) => new Set(prev).add(currentStep));
    if (currentStep < 5) {
      setCurrentStep((currentStep + 1) as AccountingCloseStep);
      scrollToTop();
    }
  };

  // Handle locking the close run
  const handleLockCloseRun = async (
    signature: string,
    notes: string
  ): Promise<boolean> => {
    if (!closeRunId) {
      setShowErrorSnackbar(true);
      return false;
    }

    try {
      const response = await lockCloseRun(
        closeRunId,
        signature,
        notes || undefined
      );

      // Update the close run state with the locked response
      const lockedCloseRun = response.closeRun;
      setCloseRun(lockedCloseRun);

      // Update the selected period to reflect its locked close
      if (
        selectedPeriod &&
        lockedCloseRun.lockedAt &&
        lockedCloseRun.lockedBy
      ) {
        setSelectedPeriod({
          ...selectedPeriod,
          periodClose: {
            closeRunId: lockedCloseRun.id,
            closeRunKey: lockedCloseRun.key,
            lockedAt: lockedCloseRun.lockedAt,
            lockedBy: lockedCloseRun.lockedBy,
          },
        });
      }

      // Refetch unmounted caches too, so the next accounting page never picks
      // its default period from a list that predates this lock.
      await queryClient.invalidateQueries({
        queryKey: ['accounting', 'periods'],
        refetchType: 'all',
      });
      // The locked close moves the latest locked period
      await queryClient.invalidateQueries({
        queryKey: ['accounting', 'initial-true-ups'],
        refetchType: 'all',
      });

      // Take the user to the period review for the period they just locked.
      navigate({
        to: '/accounting/period-review',
        search: { period: toPeriodKey(response.closeRun.period) },
      });

      return true;
    } catch (error) {
      const apiError = handleAPIError(error as AxiosError);
      setErrorMessage(null);
      setShowErrorSnackbar(false);

      if (
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.accounting_period_already_locked
      ) {
        setErrorMessage(
          'This period has already been locked. Please refresh the page to see the current status.'
        );
      } else if (
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.accounting_initial_true_up_required
      ) {
        // This close cannot lock even after the initial true-up, so name both steps.
        setErrorMessage(
          'A currency now needs an initial true-up. Complete the initial true-up, then start a new close for this period.'
        );
      } else {
        // Curated codes are handled and skip Sentry; the rest report.
        const curated = getUserFacingErrorMessage(apiError);
        if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
          setErrorMessage(curated);
        } else {
          if (!apiError.sentToSentry) {
            captureException(error);
          }

          setShowErrorSnackbar(true);
        }
      }

      return false;
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as AccountingCloseStep);
      scrollToTop();
    }
  };

  const handleStepClick = (step: AccountingCloseStep) => {
    const maxAccessibleStep = Math.max(
      ...Array.from(completedSteps),
      1
    ) as AccountingCloseStep;
    if (step <= maxAccessibleStep + 1) {
      setCurrentStep(step);
      scrollToTop();
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <VerifyDetailsStep
            onNext={handleNext}
            isCreatingCloseRun={isCreatingCloseRun}
            isPeriodLocked={
              selectedPeriod ? hasLockedActivity(selectedPeriod) : false
            }
            isPeriodNotCompleted={isPeriodNotCompleted}
            hasExistingCloseRun={closeRunId !== null}
          />
        );
      case 2:
        return (
          <InventoryRollforwardStep
            entries={reviewState.inventoryEntries}
            buckets={closeRun?.byCurrency ?? []}
            onEntriesChange={updateInventoryEntries}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <FixedAssetRollforwardStep
            entries={reviewState.fixedAssetEntries}
            buckets={closeRun?.byCurrency ?? []}
            onEntriesChange={updateFixedAssetEntries}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <PriorPeriodAdjustmentsStep
            entries={reviewState.adjustmentEntries}
            buckets={closeRun?.byCurrency ?? []}
            onEntriesChange={updateAdjustmentEntries}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 5:
        return selectedPeriod ? (
          <ConfirmLockStep
            period={selectedPeriod}
            closeRun={closeRun}
            reviewState={reviewState}
            onBack={handleBack}
            onLock={handleLockCloseRun}
          />
        ) : null;
      default:
        return null;
    }
  };

  // Show loading state while checking initial true-up status
  if (isLoadingInitialTrueUp) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Determine if we should show the period selector (only after initial true-up is completed)
  const showPeriodSelector = hasLockedInitialTrueUp;

  // Determine what content to show below the header
  const renderContent = () => {
    // Guard: Initial true-up not completed
    if (!hasLockedInitialTrueUp) {
      return (
        <Box sx={{ px: 2 }}>
          <Alert severity="warning">
            <AlertTitle>Initial true-up required</AlertTitle>
            <Typography variant="body1">
              Before you can close monthly accounting periods, you must complete
              the initial true-up to establish Hardfin as your authoritative
              subledger.
            </Typography>
            <Box sx={{ mt: 2, mb: 1 }}>
              <Button
                variant="contained"
                color="primary"
                component={Link}
                to="/accounting/initial-true-up"
              >
                Complete initial true-up
              </Button>
            </Box>
          </Alert>
        </Box>
      );
    }

    // Guard: Selected period is already locked
    if (selectedPeriod && hasLockedActivity(selectedPeriod)) {
      const periodKey = toPeriodKey(selectedPeriod);

      // A period can hold initial true-ups for several currencies as well as its close, so show each.
      const truedUpCurrencies = selectedPeriod.initialTrueUps.map(
        (initialTrueUp) => initialTrueUp.currency
      );
      return (
        <Alert severity="info" icon={<InfoOutlined />} sx={{ p: 2 }}>
          <AlertTitle paragraph>
            {formatPeriod(selectedPeriod)} is already locked
          </AlertTitle>
          {truedUpCurrencies.length > 0 && (
            <Typography variant="body1" paragraph>
              {truedUpCurrencies.join(' and ')}{' '}
              {truedUpCurrencies.length === 1 ? 'was' : 'were'} trued up in this
              period.
            </Typography>
          )}
          {selectedPeriod.periodClose && (
            <Typography variant="body1" paragraph>
              This period has been closed. You can review the close details on
              the period review page.
            </Typography>
          )}
          <Stack direction="row" spacing={1}>
            {truedUpCurrencies.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                component={Link}
                to="/accounting/initial-true-up"
              >
                View initial true-ups
              </Button>
            )}
            {selectedPeriod.periodClose && (
              <Link
                to="/accounting/period-review"
                search={{ period: periodKey }}
              >
                <Button variant="contained" color="primary" size="small">
                  View period review
                </Button>
              </Link>
            )}
          </Stack>
        </Alert>
      );
    }

    // Guard: Period out of sequence
    if (isPeriodNotSequential) {
      return (
        <Alert severity="warning" sx={{ p: 2 }}>
          <AlertTitle paragraph>Period out of sequence</AlertTitle>
          <Typography variant="body1" paragraph>
            You must close periods in order. Please select the current period
            (the next unlocked period after your most recently locked period).
          </Typography>
        </Alert>
      );
    }

    // An asset currency with no initial true-up blocks before any data is confirmed.
    if (missingInitialTrueUpCurrencies.length > 0) {
      return (
        <Alert
          severity="warning"
          sx={{
            p: 2,
            // The action slot is a flex item beside a paragraph that grows with the
            // number of currencies; without this it gives up width and the label wraps.
            '& .MuiAlert-action': {
              alignItems: 'center',
              pt: 0,
              flexShrink: 0,
            },
          }}
          action={
            <Button
              variant="contained"
              color="primary"
              size="small"
              component={Link}
              to="/accounting/initial-true-up"
              sx={{ whiteSpace: 'nowrap' }}
            >
              Complete initial true-up
            </Button>
          }
        >
          <AlertTitle>Cannot review and lock period</AlertTitle>
          <Typography variant="body1">
            {missingInitialTrueUpCurrencies.map((iso) => (
              <span key={iso}>
                Your organization has {iso} assets but does not have an initial
                true-up for {iso} yet.{' '}
              </span>
            ))}
            Complete all initial true-ups before closing this period.
          </Typography>
        </Alert>
      );
    }

    // Normal workflow content
    return (
      <>
        <Card sx={{ boxShadow: theme.shadows[6] }} variant="outlined">
          <CardContent sx={{ paddingTop: 3 }}>
            <Stepper
              activeStep={currentStep - 1}
              alternativeLabel
              connector={<CustomConnector />}
            >
              {STEPS.map((step) => {
                const isCompleted = completedSteps.has(step.id);
                const maxAccessibleStep = Math.max(
                  ...Array.from(completedSteps),
                  1
                ) as AccountingCloseStep;
                const isAccessible = step.id <= maxAccessibleStep + 1;

                return (
                  <Step key={step.id} completed={isCompleted}>
                    <StepButton
                      onClick={() => handleStepClick(step.id)}
                      disabled={!isAccessible}
                    >
                      <StepLabel
                        StepIconComponent={CustomStepIcon}
                        optional={
                          <Typography variant="caption">
                            {step.description}
                          </Typography>
                        }
                      >
                        {step.label}
                      </StepLabel>
                    </StepButton>
                  </Step>
                );
              })}
            </Stepper>
          </CardContent>
        </Card>

        {!!errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)}>
            <AlertTitle>Cannot review and lock period</AlertTitle>
            {errorMessage}
          </Alert>
        )}

        {/* Show loading interstitial when creating close run */}
        {isCreatingCloseRun && <CloseRunLoader />}

        {/* Show step content when not creating close run */}
        {selectedPeriod && !isCreatingCloseRun && (
          <Box>{renderStepContent()}</Box>
        )}

        {closeRun && (
          <CloseRunInfoBar
            closeRunKey={closeRun.key}
            createdAt={closeRun.createdAt}
            period={closeRun.period}
            createdBy={closeRun.createdBy}
          />
        )}
      </>
    );
  };

  return (
    <Stack spacing={3}>
      {/* Header - always rendered consistently */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        ref={topAnchorRef}
      >
        <Stack spacing={1}>
          <ReportBreadcrumbs
            title="Review and lock period"
            parent="accounting"
          />
          <Typography variant="h5">Review and lock period</Typography>
        </Stack>

        {showPeriodSelector && (
          <Stack direction="row" spacing={2} alignItems="center">
            {closeRun && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<DownloadRounded fontSize="small" />}
                disabled={!closeRun.excelFileKey || downloadSummary.isPending}
                onClick={() => downloadSummary.mutate(closeRun.excelFileKey)}
              >
                Download complete period summary
              </Button>
            )}
            <AccountingPeriodSelector
              onPeriodChange={handlePeriodChange}
              initialTrueUpPeriod={
                initialTrueUpYear !== undefined &&
                initialTrueUpMonth !== undefined
                  ? { year: initialTrueUpYear, month: initialTrueUpMonth }
                  : undefined
              }
            />
          </Stack>
        )}
      </Stack>

      {/* Content - varies based on state */}
      {renderContent()}

      <SupportSnackbar
        isOpen={showErrorSnackbar}
        onClose={() => setShowErrorSnackbar(false)}
        message="An error occurred while processing your request."
      />
    </Stack>
  );
}
